import { AuthRedirectService } from '@edtech-ui/core/services/auth-redirect/auth-redirect.service';
import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { LoginPayload } from '@edtech-ui/core/models/interfaces/login';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';
import { FireBaseServiceService } from '@edtech-ui/core/services/firebaseService/fire-base-service.service';
import { OneSignalService } from '@edtech-ui/core/services/one-signal-service/one-signal.service';
import { SharedModule } from '@edtech-ui/shared/shared.module';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';

@Component({
	selector: 'app-user-registration-login-email',
	standalone: true,
	imports: [SharedModule, CommonModule],
	templateUrl: './user-registration-login-email.component.html',
	styleUrls: ['../user-registration-login-with-phone/user-registration-login-with-phone.component.scss']

})
export class UserRegistrationLoginEmailComponent implements OnInit, OnDestroy {

	loginForm!: FormGroup;
	subscription = new Subscription();
	isPasswordFocused: boolean = false;
	isEmailFocused: boolean = false;
	isLoading: boolean = false;
	userEmail: string | null = null;
	resendTimer: number = 120;
	intervalId: any;
	canResendOtp: boolean = false;
	displayTime: string = '02:00';
	fcmToken: string = '';
	redirectUrl:string = '';

	constructor(
		private router: Router,
		private _toastr: ToastrService,
		private _authService: AuthServiceService,
		private route :ActivatedRoute,
		private _fireBaseService : FireBaseServiceService,
		private _oneSignalService : OneSignalService,
		private _authRedirectService: AuthRedirectService
	) {
		this.loginForm = new FormGroup({
			otp: new FormControl('', [Validators.required, Validators.pattern(/^[0-9]{6}$/)])
		});
	}

	ngOnInit(): void {
		this.route.queryParams.subscribe(params => {
			this.redirectUrl = params['redirectUrl'] || '';
			this._authRedirectService.redirectIfLoggedIn(this.redirectUrl);
		});
		this.userEmail = this._authService.getEmail();
		this.startResendOtpTimer();
		this.fcmToken = this._authService.getFcmToken() || '';
	}

	async onLogin() {
		// Guard: drop any click that arrives while a request is already in-flight.
		// Critical here because the async awaits below create a window where
		// isLoading was not yet true, allowing multiple clicks to each fire a signin call.
		if (this.isLoading) return;

		if (!this.userEmail) {
			return;
		}

		// Set loading immediately BEFORE any async work so the button is
		// disabled and no further clicks can pass the guard above.
		this.isLoading = true;

		const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Unknown';
		const language = navigator.language || '';
		const country = language.includes('-') ? language.split('-')[1] : 'Unknown';
		const fcmToken = (await Promise.race([
			this._fireBaseService.getFCMToken(),
			new Promise((resolve) => setTimeout(() => resolve(''), 5000))
		])) as string || '';
		const playerId = await this._oneSignalService.getPlayerId() || '';

		if (this.loginForm.valid) {
			const loginPayload: LoginPayload = {
				email: this.userEmail,
				otp: this.loginForm.value.otp,
				fcm_token: fcmToken,
				is_default: true,
				login_source: "web",
				user_timezone: timeZone,
				country: country,
				onesignal_player_id :playerId
			};

			this.subscription.add(
				this._authService.postLogin(loginPayload).subscribe({
					next: (response) => {
						this.isLoading = false;

						if (!response.Success) {
							this._toastr.error(response.Message || 'Login failed.');
							return;
						}

						if (response.Data?.phone_email) {
							this._authService.currentUserEmail.next(response.Data.phone_email);
							localStorage.setItem('email', response.Data.phone_email);
						}

						if (response.Success && response.Data?.refresh_token && response.Data?.token || response.Status === 200) {
							this._authService.setToken(response.Data.token);
							localStorage.setItem('refresh_token', response.Data.refresh_token);
							this._toastr.success(response.Message);

							if (response.Data?.modules) {
								this._authService.setModules(response.Data.modules);
							}

							const pendingRoundId = localStorage.getItem('pending_practice_round_id');
							if (pendingRoundId && (response.Data?.role_id === 2 || response.Data?.role_id === 1)) {
								localStorage.removeItem('pending_practice_round_id');
								this.router.navigate(['/student-dashboard/ai-interview-dashboard']);
							} else {
								this._authRedirectService.handlePostLoginRedirect(this.redirectUrl);
							}
						} else {
							this.router.navigate(['/login']);
							this._toastr.error(response.Message);
						}
					},
					error: (err: any) => {
						this.isLoading = false;
						this._toastr.error(err?.error?.Message);
					}
				})
			);
		} else {
			// Form invalid — reset loading so button is re-enabled
			this.isLoading = false;
		}
	}

	onEmailFocus(): void {
		this.isEmailFocused = true;
	}

	onEmailBlur(): void {
		this.isEmailFocused = !!this.loginForm.controls['otp'].value;
	}

	getEmailPlaceholder(): string {
		return this.isEmailFocused ? '' : 'Enter otp';
	}

	shouldFloatEmailLabel(): boolean {
		return this.isEmailFocused || !!this.loginForm.controls['otp'].value;
	}

	startResendOtpTimer() {
		if (this.intervalId) {
			clearInterval(this.intervalId);
		}

		this.canResendOtp = false;
		this.resendTimer = 120;

		this.updateDisplayTime();

		this.intervalId = setInterval(() => {
			this.resendTimer--;

			this.updateDisplayTime();

			if (this.resendTimer <= 0) {
				clearInterval(this.intervalId);
				this.canResendOtp = true;
			}
		}, 1000);
	}

	updateDisplayTime() {
		const minutes = Math.floor(this.resendTimer / 60);
		const seconds = this.resendTimer % 60;
		this.displayTime = `${minutes < 10 ? '0' : ''}${minutes}:` + `${seconds < 10 ? '0' : ''}${seconds}`;
	}

	resendOtp() {
		if (!this.userEmail) {
			return;
		}

		this.isLoading = true;
		const loginPayload: LoginPayload = {
			email: this.userEmail,
			fcm_token: this.fcmToken,
			is_default: true,
			login_source: "web"
		};

		this.subscription.add(
			this._authService.postLogin(loginPayload).subscribe({
				next: (response) => {
					this.isLoading = false;
					if (response.Success || response.Status === 200) {
						this._toastr.success(response.Message);
						this.startResendOtpTimer();
					} else {
						this._toastr.error(response.Message);
					}
				},
				error: (err: any) => {
					this.isLoading = false;
					this._toastr.error(err?.error?.Message);
				}
			})
		);
	}

	goToLoginPage() {
		this.router.navigate(['/login-phone'], { queryParams: { email: this.userEmail } })
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
		if (this.intervalId) {
			clearInterval(this.intervalId);
		}
	}

}
